// HEIC is the iPhone camera format. Safari and the iOS app decode it natively,
// but Chrome, Firefox and Edge cannot render it at all, so a licence photo
// uploaded from the mobile app shows as a broken image everywhere else.
//
// The web dropzones already convert HEIC on upload; this handles files that
// reached storage some other way (the mobile app, including builds already
// installed on workers' phones).
const HEIC_PATTERN = /\.hei[cf](\?|#|$)|image\/hei[cf]/i

export function isHeicFile(file) {
    if (!file) return false
    if (typeof file === 'string') return HEIC_PATTERN.test(file)
    return [file.name, file.filePath, file.storagePath, file.path, file.mimeType, file.contentType, file.url]
        .filter(Boolean)
        .some(value => HEIC_PATTERN.test(String(value)))
}

// heic2any is 1.3 MB and starts a Web Worker the moment it is imported, yet almost
// no page view ever involves a HEIC. Load it only when one actually turns up, so
// it stays out of the main bundle. A failed chunk load is not cached, so the next
// attempt retries instead of failing forever.
let converterPromise = null
const loadConverter = () => {
    converterPromise = converterPromise || import('heic2any')
        .then(module => module.default)
        .catch(error => { converterPromise = null; throw error })
    return converterPromise
}

// Converts a HEIC blob into a single image blob. heic2any returns only the first
// image unless `multiple: true` is passed, which this never does.
export async function convertHeicBlob(blob, toType = 'image/jpeg', quality = 0.9) {
    const heic2any = await loadConverter()
    return heic2any({ blob, toType, quality })
}

// Converts the HEIC at `url` into a JPEG object URL the browser can render.
// The caller owns the returned URL and must revoke it when done.
export async function heicToJpegObjectUrl(url) {
    const response = await fetch(url)
    if (!response.ok) throw new Error(`Could not load image (${response.status})`)
    return URL.createObjectURL(await convertHeicBlob(await response.blob()))
}
